import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { FaEye, FaEyeSlash, FaCheck, FaTimes, FaUser, FaBuilding, FaMapMarkerAlt, FaUserTag, FaClipboardList } from 'react-icons/fa';

export default function Register() {
  const { t } = useTranslation();
  const { API, setUser } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    // 1. Personal Info
    civility: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    profession: '',
    
    // 2. Company
    hasCompany: false,
    companyName: '',
    activityType: '',
    siret: '',
    tvaNumber: '',
    website: '',
    companySize: '',
    
    // 3. Address
    address: '',
    postalCode: '',
    city: '',
    country: 'France',
    
    // 4. Account Type
    accountType: '',
    
    // 5. Needs
    needs: []
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Activity types
  const activityTypes = [
    "Bureau d'etudes geotechniques",
    "Entreprise de forage",
    "Entreprise de fondations speciales",
    "Fournisseur d'equipements",
    "Laboratoire",
    "Service",
    "Autre"
  ];

  // Company sizes
  const companySizes = [
    "1-10 employes",
    "11-50 employes",
    "51-200 employes",
    "200+ employes"
  ];

  // Account types
  const accountTypes = [
    { value: "client", label: "🛒 Client (je cherche du materiel)" },
    { value: "supplier", label: "🏭 Fournisseur (je vends/loue du materiel)" },
    { value: "service", label: "🔧 Service (prestataire)" },
    { value: "other", label: "🔄 Autre" }
  ];

  // Needs options
  const needsOptions = [
    { value: "buy_new", label: "Achat de materiel neuf" },
    { value: "buy_used", label: "Achat de materiel d'occasion" },
    { value: "rental", label: "Location de materiel" },
    { value: "sell", label: "Vente de materiel" },
    { value: "services", label: "Services/Prestations" }
  ];

  // Password validation rules
  const passwordRules = [
    { label: 'Au moins 8 caracteres', test: (p) => p.length >= 8 },
    { label: 'Une majuscule', test: (p) => /[A-Z]/.test(p) },
    { label: 'Une minuscule', test: (p) => /[a-z]/.test(p) },
    { label: 'Un chiffre', test: (p) => /\d/.test(p) }
  ];

  const isPasswordValid = passwordRules.every(rule => rule.test(formData.password));

  const handleNeedChange = (need) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs.includes(need) 
        ? prev.needs.filter(n => n !== need)
        : [...prev.needs, need]
    }));
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        if (!formData.civility || !formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        if (!isPasswordValid) {
          toast.error('Le mot de passe ne respecte pas les criteres de securite');
          return false;
        }
        return true;
      case 2:
        if (formData.hasCompany && !formData.companyName) {
          toast.error('Veuillez indiquer le nom de votre entreprise');
          return false;
        }
        return true;
      case 3:
        if (!formData.address || !formData.postalCode || !formData.city || !formData.country) {
          toast.error('Veuillez remplir tous les champs d\'adresse');
          return false;
        }
        return true;
      case 4:
        if (!formData.accountType) {
          toast.error('Veuillez selectionner un type de compte');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;
    
    setLoading(true);

    try {
      // Prepare data for API
      const submitData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        civility: formData.civility,
        first_name: formData.firstName,
        last_name: formData.lastName,
        profession: formData.profession,
        has_company: formData.hasCompany,
        company: formData.companyName,
        activity_type: formData.activityType,
        siret: formData.siret,
        tva_number: formData.tvaNumber,
        website: formData.website,
        company_size: formData.companySize,
        address: formData.address,
        postal_code: formData.postalCode,
        city: formData.city,
        country: formData.country,
        account_type: formData.accountType,
        needs: formData.needs
      };

      const response = await axios.post(`${API}/auth/register`, submitData);
      const { token, user, pending_approval } = response.data;
      
      // Store token and user
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      if (pending_approval) {
        toast.success('Compte cree ! Votre inscription est en attente d\'approbation par un administrateur.');
      } else {
        toast.success('Compte cree avec succes ! Bienvenue 🎉');
      }
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail;
      
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach(err => toast.error(err.msg || err));
      } else {
        toast.error(errorMessage || 'Erreur lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  // Progress bar
  const ProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {[1, 2, 3, 4, 5].map(step => (
          <div 
            key={step}
            className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold transition-all ${
              currentStep >= step 
                ? 'bg-[#635BFF] text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step}
          </div>
        ))}
      </div>
      <div className="h-2 bg-gray-200 rounded-full">
        <div 
          className="h-2 bg-[#635BFF] rounded-full transition-all"
          style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>Identite</span>
        <span>Entreprise</span>
        <span>Adresse</span>
        <span>Compte</span>
        <span>Besoins</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#0A2540] flex items-center justify-center py-8 px-4" data-testid="register-page">
      <div className="w-full max-w-2xl">
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold mb-2 text-center text-[#0A2540]" data-testid="register-title">
              Creer un compte
            </h1>
            <p className="text-gray-500 text-center mb-6">
              Rejoignez la plateforme GEO TS CONSULTING
            </p>
            
            <ProgressBar />
            
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2 mb-4 text-[#635BFF]">
                    <FaUser />
                    <h2 className="text-lg font-semibold">Informations personnelles</h2>
                  </div>
                  
                  {/* Civility */}
                  <div>
                    <Label>Civilite *</Label>
                    <RadioGroup 
                      value={formData.civility}
                      onValueChange={(value) => setFormData({...formData, civility: value})}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="M." id="mr" />
                        <Label htmlFor="mr" className="cursor-pointer">M.</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Mme" id="mme" />
                        <Label htmlFor="mme" className="cursor-pointer">Mme</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  {/* First & Last Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Prenom *</Label>
                      <Input
                        id="firstName"
                        required
                        placeholder="Jean"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Nom *</Label>
                      <Input
                        id="lastName"
                        required
                        placeholder="Dupont"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div>
                    <Label htmlFor="email">Email professionnel *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      placeholder="jean.dupont@entreprise.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone">Telephone portable *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      placeholder="+33 6 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  
                  {/* Profession */}
                  <div>
                    <Label htmlFor="profession">Profession</Label>
                    <Input
                      id="profession"
                      placeholder="Ingenieur geotechnicien, Chef de projet..."
                      value={formData.profession}
                      onChange={(e) => setFormData({...formData, profession: e.target.value})}
                    />
                  </div>
                  
                  {/* Password */}
                  <div>
                    <Label htmlFor="password">Mot de passe *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={() => setPasswordFocused(false)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    
                    {(passwordFocused || formData.password) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-2">Criteres du mot de passe :</p>
                        <ul className="space-y-1">
                          {passwordRules.map((rule, index) => (
                            <li key={index} className={`flex items-center gap-2 ${rule.test(formData.password) ? 'text-[#635BFF]' : 'text-gray-500'}`}>
                              {rule.test(formData.password) ? (
                                <FaCheck className="text-[#635BFF]" />
                              ) : (
                                <FaTimes className="text-gray-400" />
                              )}
                              {rule.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Company */}
              {currentStep === 2 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2 mb-4 text-[#635BFF]">
                    <FaBuilding />
                    <h2 className="text-lg font-semibold">Entreprise</h2>
                  </div>
                  
                  {/* Has Company */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="hasCompany"
                        checked={formData.hasCompany}
                        onCheckedChange={(checked) => setFormData({...formData, hasCompany: checked})}
                      />
                      <Label htmlFor="hasCompany" className="cursor-pointer font-medium">
                        J'ai une entreprise / Je represente une entreprise
                      </Label>
                    </div>
                  </div>
                  
                  {formData.hasCompany && (
                    <div className="space-y-4 pt-4 border-t">
                      {/* Company Name */}
                      <div>
                        <Label htmlFor="companyName">Nom de l'entreprise *</Label>
                        <Input
                          id="companyName"
                          placeholder="Nom de votre entreprise"
                          value={formData.companyName}
                          onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                        />
                      </div>
                      
                      {/* Activity Type */}
                      <div>
                        <Label>Type d'activite</Label>
                        <Select 
                          value={formData.activityType}
                          onValueChange={(value) => setFormData({...formData, activityType: value})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Selectionnez votre activite" />
                          </SelectTrigger>
                          <SelectContent>
                            {activityTypes.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* SIRET & TVA */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="siret">Numero SIRET</Label>
                          <Input
                            id="siret"
                            placeholder="14 chiffres"
                            maxLength={14}
                            value={formData.siret}
                            onChange={(e) => setFormData({...formData, siret: e.target.value.replace(/\D/g, '')})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="tvaNumber">N° TVA Intracommunautaire</Label>
                          <Input
                            id="tvaNumber"
                            placeholder="FR XX XXX XXX XXX"
                            value={formData.tvaNumber}
                            onChange={(e) => setFormData({...formData, tvaNumber: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      {/* Website */}
                      <div>
                        <Label htmlFor="website">Site web</Label>
                        <Input
                          id="website"
                          type="url"
                          placeholder="https://www.votre-entreprise.com"
                          value={formData.website}
                          onChange={(e) => setFormData({...formData, website: e.target.value})}
                        />
                      </div>
                      
                      {/* Company Size */}
                      <div>
                        <Label>Taille de l'entreprise</Label>
                        <Select 
                          value={formData.companySize}
                          onValueChange={(value) => setFormData({...formData, companySize: value})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Nombre d'employes" />
                          </SelectTrigger>
                          <SelectContent>
                            {companySizes.map(size => (
                              <SelectItem key={size} value={size}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Address */}
              {currentStep === 3 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2 mb-4 text-[#635BFF]">
                    <FaMapMarkerAlt />
                    <h2 className="text-lg font-semibold">Adresse</h2>
                  </div>
                  
                  {/* Address */}
                  <div>
                    <Label htmlFor="address">Adresse *</Label>
                    <Input
                      id="address"
                      required
                      placeholder="123 Rue de la Geotechnique"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                  
                  {/* Postal Code & City */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postalCode">Code postal *</Label>
                      <Input
                        id="postalCode"
                        required
                        placeholder="75020"
                        value={formData.postalCode}
                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Ville *</Label>
                      <Input
                        id="city"
                        required
                        placeholder="Paris"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  {/* Country */}
                  <div>
                    <Label htmlFor="country">Pays *</Label>
                    <Select 
                      value={formData.country}
                      onValueChange={(value) => setFormData({...formData, country: value})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selectionnez un pays" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="France">France</SelectItem>
                        <SelectItem value="Belgique">Belgique</SelectItem>
                        <SelectItem value="Suisse">Suisse</SelectItem>
                        <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                        <SelectItem value="Maroc">Maroc</SelectItem>
                        <SelectItem value="Algerie">Algerie</SelectItem>
                        <SelectItem value="Tunisie">Tunisie</SelectItem>
                        <SelectItem value="Senegal">Senegal</SelectItem>
                        <SelectItem value="Cote d'Ivoire">Cote d'Ivoire</SelectItem>
                        <SelectItem value="Cameroun">Cameroun</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="Autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 4: Account Type */}
              {currentStep === 4 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2 mb-4 text-[#635BFF]">
                    <FaUserTag />
                    <h2 className="text-lg font-semibold">Type de compte</h2>
                  </div>
                  
                  <p className="text-gray-500 text-sm mb-4">
                    Selectionnez le type de compte qui correspond le mieux a votre activite
                  </p>
                  
                  <RadioGroup 
                    value={formData.accountType}
                    onValueChange={(value) => setFormData(prev => ({...prev, accountType: value}))}
                    className="space-y-3"
                  >
                    {accountTypes.map(type => (
                      <div 
                        key={type.value}
                        className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all hover:border-[#635BFF] ${
                          formData.accountType === type.value ? 'border-[#635BFF] bg-[#635BFF]/5' : 'border-gray-200'
                        }`}
                      >
                        <RadioGroupItem value={type.value} id={`account-${type.value}`} />
                        <Label htmlFor={`account-${type.value}`} className="cursor-pointer flex-1 text-base">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Step 5: Needs */}
              {currentStep === 5 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-center gap-2 mb-4 text-[#635BFF]">
                    <FaClipboardList />
                    <h2 className="text-lg font-semibold">Vos besoins</h2>
                  </div>
                  
                  <p className="text-gray-500 text-sm mb-4">
                    Selectionnez vos besoins principaux (plusieurs choix possibles)
                  </p>
                  
                  <div className="space-y-3">
                    {needsOptions.map(need => (
                      <div 
                        key={need.value}
                        className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-all hover:border-[#635BFF] ${
                          formData.needs.includes(need.value) ? 'border-[#635BFF] bg-[#635BFF]/5' : 'border-gray-200'
                        }`}
                        onClick={() => handleNeedChange(need.value)}
                      >
                        <Checkbox 
                          id={`need-${need.value}`}
                          checked={formData.needs.includes(need.value)}
                        />
                        <span className="flex-1">{need.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                {currentStep > 1 ? (
                  <Button type="button" variant="outline" onClick={prevStep}>
                    Precedent
                  </Button>
                ) : (
                  <div></div>
                )}
                
                {currentStep < 5 ? (
                  <Button type="button" onClick={nextStep} className="bg-[#635BFF] hover:bg-[#5449e6]">
                    Suivant
                  </Button>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={loading} 
                    className="bg-[#635BFF] hover:bg-[#5449e6]"
                  >
                    {loading ? 'Creation en cours...' : 'Creer mon compte'}
                  </Button>
                )}
              </div>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-gray-500">
                Deja un compte ?{' '}
                <Link to="/login" className="text-[#0A2540] font-medium hover:underline">
                  Se connecter
                </Link>
              </p>
            </div>
            
            <p className="mt-4 text-xs text-gray-400 text-center">
              En creant un compte, vous acceptez nos{' '}
              <Link to="/terms-of-service" className="underline">Conditions d'utilisation</Link>
              {' '}et notre{' '}
              <Link to="/privacy-policy" className="underline">Politique de confidentialite</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
